import moment, {Moment} from 'moment-timezone'

import type {MaterialsHistory, MaterialWithRecipes} from '../types'

import {useMaterialsHistory} from './useMaterialsHistory'

/*
 * Builds a record of the latest relevant material history entry per asset for the given range.
 * Only the `to` date is checked, because the first history entry may be outside the range - in
 * that case we take the newest entry available before the range. Entries are sorted ascending.
 */
const getMaterialsByIdFromHistory = (
  materialsHistory: MaterialsHistory,
  to: Moment
): Record<string, MaterialWithRecipes> => {
  const materialsById: Record<string, MaterialWithRecipes> = {}
  for (const assetHistory of Object.values(materialsHistory)) {
    // Entries are sorted ascending, so the last entry before `to` is the newest relevant one.
    const relevantEntry = assetHistory
      .filter((entry) => moment.utc(entry.updatedOn ?? entry.createdOn).isBefore(to))
      .at(-1)
    if (relevantEntry) {
      materialsById[String(relevantEntry.id)] = relevantEntry
    }
  }
  return materialsById
}

interface UseHistoryMaterialsByIdParams {
  plantId: string
  timeFrame: [Moment, Moment]
}

export const useHistoryMaterialsById = ({
  plantId,
  timeFrame: [start, end]
}: UseHistoryMaterialsByIdParams) =>
  useMaterialsHistory<Record<string, MaterialWithRecipes>>({
    plantCode: plantId,
    from: start.toISOString(),
    to: end.toISOString(),
    options: {
      select: (materialsHistory) => getMaterialsByIdFromHistory(materialsHistory, end)
    }
  })
